import { Box, Divider, Grid, IconButton, Typography } from "@mui/material";
import { colors } from "../../theme/muiColors";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PaginationLoadingOverlay } from "../clients/PaginationLoadingOverlay";

/** Helpers visuales (mismo estilo que Commercial) */
const money = (v) =>
  typeof v === "number"
    ? v.toLocaleString("en-US", { style: "currency", currency: "USD" })
    : v;

const Title = ({ children }) => (
  <Typography sx={{ fontWeight: "bold", fontSize: 16, mt: 2, mb: 1 }}>
    {children}
  </Typography>
);

const Row = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
    <Typography sx={{ fontSize: 14, color: colors.blueGrey[800] }}>{label}</Typography>
    <Typography sx={{ fontSize: 14 }}>{value}</Typography>
  </Box>
);

const Card = ({ children, sx }) => (
  <Box
    sx={{
      p: 2,
      border: `1px solid ${colors.grey[200]}`,
      borderRadius: 1,
      bgcolor: "var(--fx-surface-base, #FFFFFF)",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const toTitle = (k = "") =>
  k
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

export const ResidentialQuoteSummary = ({ quote, calculator = false, handleResidentialTab, loading = false }) => {
  // coverage.surcharge puede venir vacío
  const surcharge = quote?.coverage?.surcharge || {};
  const extraMilesRate = Number(surcharge?.extraMilesRate || 0);
  const ignoredAreaRate = Number(surcharge?.ignoredAreaRate || 0);
  const showSurcharges = extraMilesRate > 0 || ignoredAreaRate > 0;

  const coverageStatus = quote?.coverage?.inCoverage
    ? "In coverage"
    : quote?.coverage?.inIgnoreArea
      ? "Ignore area"
      : "Out of coverage";
      
  if (loading) {
    return <PaginationLoadingOverlay isVisible={loading} />
  }

  return (
    <>
      {calculator && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => handleResidentialTab(0)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Quote Summary
          </Typography>
        </Box>
      )}

      <Grid container spacing={2} sx={{ p: 2 }}>
        {/* Columna principal */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>
              General
            </Typography>
            {quote?.sqftRange && (
              <Row
                label="Sqft range"
                value={`${quote.sqftRange.minSqft} – ${quote.sqftRange.maxSqft}`}
              />
            )}
            {quote?.basePrice != null && (
              <Row label="Base price" value={money(quote.basePrice)} />
            )}
          </Card>

          {/* Coverage */}
          {quote?.coverage && (
            <Card sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>
                Coverage
              </Typography>

              <Row label="Status" value={coverageStatus} />

              {quote.coverage.nearestArea?.name && (
                <Row label="Nearest area" value={quote.coverage.nearestArea.name} />
              )}

              {quote.coverage.distanceMiles != null && (
                <Row label="Distance (mi)" value={quote.coverage.distanceMiles} />
              )}

              {quote.coverage.milesOutside != null && (
                <Row label="Miles outside" value={quote.coverage.milesOutside} />
              )}

              {quote.coverage.adjustedMiles != null && (
                <Row label="Adjusted miles" value={quote.coverage.adjustedMiles} />
              )}

              {showSurcharges && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: 13 }}>
                    Surcharges
                  </Typography>
                  {extraMilesRate > 0 && (
                    <Row
                      label="Coverage Area Rate (per mile)"
                      value={money(extraMilesRate)}
                    />
                  )}
                  {ignoredAreaRate > 0 && (
                    <Row
                      label="Ignore Area Rate"
                      value={money(ignoredAreaRate)}
                    />
                  )}
                </>
              )}

              {quote.coverage.rateApplied != null && (
                <Row label="Rate applied" value={quote.coverage.rateApplied} />
              )}
              {quote.coverage.fee != null && (
                <Row label="Fee" value={money(quote.coverage.fee)} />
              )}
            </Card>
          )}

          {/* Rooms */}
          {quote?.rooms && (
            <>
              <Title>Rooms</Title>
              <Card>
                {Object.entries(quote.rooms).map(([key, val]) => {
                  const unitOrAmount =
                    val?.amount ?? val?.unit ?? 0;
                  const qty = val?.quantity ?? 0;
                  const label = `${toTitle(key)} ${money(unitOrAmount)}`;
                  return (
                    <Row key={key} label={label} value={money(val?.subtotal)} />
                  );
                })}
              </Card>
            </>
          )}

          {/* Areas */}
          {quote?.areas && (
            <>
              <Title>Areas</Title>
              <Card>
                {Object.entries(quote.areas).map(([key, val]) => {
                  const sel = val?.selected ? " (selected)" : "";
                  return (
                    <Row
                      key={key}
                      label={`${toTitle(key)}${sel}`}
                      value={money(val?.subtotal)}
                    />
                  );
                })}
              </Card>
            </>
          )}

          {/* Additional Requests */}
          {quote?.additionalRequest && (
            <>
              <Title>Additional Requests</Title>
              <Card>
                {Object.entries(quote.additionalRequest).map(([key, val]) => {
                  const sel = val?.selected ? " (selected)" : "";
                  return (
                    <Row
                      key={key}
                      label={`${toTitle(key)}${sel}`}
                      value={money(val?.subtotal)}
                    />
                  );
                })}
              </Card>
            </>
          )}

          {/* Pets */}
          {quote?.pets && (
            <>
              <Title>Pets</Title>
              <Card>
                <Row
                  label={`Pets ${quote.pets.selected ? "(selected)" : ""}`}
                  value={money(quote.pets.subtotal)}
                />
              </Card>
            </>
          )}
        </Grid>

        {/* Totales */}
        <Grid item xs={12} lg={4} sx={{ mt: { xs: 0, lg: 2 } }}>
          <Card sx={{ maxWidth: 380, position: { lg: "sticky" }, top: 16 }}>
            <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>
              Totals
            </Typography>
            <Row label="Subtotal" value={money(quote?.subtotal)} />
            <Row label="Total" value={money(quote?.total)} />
          </Card>
        </Grid>
      </Grid>
    </>

  );
};
